import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, BadgeEuro, ShieldCheck, Wallet, Link2, BadgeCheck } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const creatorMode = location.pathname === "/creator/signup";
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [profileUrl, setProfileUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(email.trim(), password, name.trim(),
      creatorMode ? { is_creator: true, handle: handle.trim(), paypal_email: paypalEmail.trim(), profile_url: profileUrl.trim() } : {});
    setLoading(false);
    if (res.ok) { toast.success(creatorMode ? "Creator account created!" : "Account created!"); navigate(creatorMode ? "/creator" : "/profile"); }
    else toast.error(res.error || "Sign up failed");
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] nerd-gradient flex items-center justify-center px-4 py-16">
      <div className={`w-full ${creatorMode ? "max-w-lg" : "max-w-md"} animate-fade-up`}>
        <div className="bg-nerd-card border border-black/8 rounded-3xl p-8 shadow-2xl">
          <div className="w-11 h-11 rounded-2xl bg-nerd-light flex items-center justify-center mb-6">
            <img src="/logo-black.png" alt="Flavour Intelligence" className="w-6 h-6 object-contain" />
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">{creatorMode ? "Become a creator" : "Create your account"}</h1>
          <p className="text-nerd-light/50 text-sm mb-7">{creatorMode ? "Share your recipes with the Flavour Intelligence community and get paid for every featured reel." : "Join Flavour Intelligence and cook smarter."}</p>

          {creatorMode && (
            <div data-testid="collab-terms" className="mb-7 rounded-2xl border border-black/8 bg-nerd-depth p-5">
              <p className="text-xs font-mono-x uppercase tracking-widest text-nerd-light/50 mb-3">How the collaboration works</p>
              <ul className="space-y-3 text-sm text-nerd-light/80">
                <li className="flex items-start gap-3">
                  <BadgeEuro className="w-4.5 h-4.5 text-nerd-light shrink-0 mt-0.5" />
                  <span><strong>€8.99 per reel</strong> — a one-time flat fee for each recipe that's reviewed, approved and featured, paid within 15 business days.</span>
                </li>
                <li className="flex items-start gap-3">
                  <ShieldCheck className="w-4.5 h-4.5 text-nerd-light shrink-0 mt-0.5" />
                  <span><strong>You keep your rights</strong> — you stay the owner of your content. You grant us a non-exclusive licence, so you're free to post it anywhere else too.</span>
                </li>
                <li className="flex items-start gap-3">
                  <BadgeCheck className="w-4.5 h-4.5 text-nerd-light shrink-0 mt-0.5" />
                  <span><strong>Always credited</strong> — your name, handle and profile link appear alongside every recipe we feature.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Wallet className="w-4.5 h-4.5 text-nerd-light shrink-0 mt-0.5" />
                  <span><strong>Paid via PayPal</strong> — payouts go to your PayPal email below. It must match your PayPal account.</span>
                </li>
              </ul>
              <p className="text-xs text-nerd-light/40 mt-4">You'll review and accept the full Creator Content License &amp; Consent Agreement before submitting each recipe.</p>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-mono-x uppercase tracking-widest text-nerd-light/50 mb-1.5 block">Name</label>
              <input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)}
                className="w-full bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint transition-colors" placeholder="Your name" />
            </div>
            {creatorMode && (
              <div>
                <label className="text-xs font-mono-x uppercase tracking-widest text-nerd-light/50 mb-1.5 block">Instagram handle</label>
                <input data-testid="register-handle" value={handle} onChange={(e) => setHandle(e.target.value)}
                  className="w-full bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint transition-colors" placeholder="@yourhandle" />
              </div>
            )}
            {creatorMode && (
              <div>
                <label className="text-xs font-mono-x uppercase tracking-widest text-nerd-light/50 mb-1.5 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Profile link</label>
                <input data-testid="register-profile-url" value={profileUrl} onChange={(e) => setProfileUrl(e.target.value)}
                  className="w-full bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint transition-colors" placeholder="https://instagram.com/yourhandle" />
              </div>
            )}
            {creatorMode && (
              <div>
                <label className="text-xs font-mono-x uppercase tracking-widest text-nerd-light/50 mb-1.5 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> PayPal email</label>
                <input data-testid="register-paypal-email" type="email" value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)}
                  className="w-full bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint transition-colors" placeholder="payouts@email.com" />
                <p className="text-xs text-nerd-light/40 mt-1.5">Where we send your €8.99 per featured reel. Must match your PayPal account.</p>
              </div>
            )}
            <div>
              <label className="text-xs font-mono-x uppercase tracking-widest text-nerd-light/50 mb-1.5 block">Email</label>
              <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint transition-colors" placeholder="you@email.com" />
            </div>
            <div>
              <label className="text-xs font-mono-x uppercase tracking-widest text-nerd-light/50 mb-1.5 block">Password</label>
              <input data-testid="register-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint transition-colors" placeholder="At least 6 characters" />
            </div>
            <button data-testid="register-submit" disabled={loading} type="submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-nerd-light text-white font-semibold px-5 py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (creatorMode ? "Create creator account" : "Create account")}
            </button>
          </form>
          <p className="text-sm text-nerd-light/50 mt-6 text-center">
            Already have an account? <Link to="/login" data-testid="go-login" className="text-nerd-light hover:underline">Log in</Link>
          </p>
          {!creatorMode && (
            <p className="text-sm text-nerd-light/50 mt-2 text-center">
              Are you a food creator? <Link to="/creator/signup" className="text-nerd-light hover:underline font-medium">Sign up as a creator</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
